/**
 * §6 Events —— cj-suggestion-slider（线下活动无限循环轮播）
 *
 * 需求来源：提示词 第1次提交 需求1「还原效果与动画细节」（DEV-124229）；原型 L8422-8671 + 内联 <script> L8674。
 *
 * 【为什么必须重写，不能照搬原型的原生 JS】
 * 原型脚本是**命令式 DOM 操作**：`card.cloneNode(true)` 造前后各一份克隆、`insertBefore` 塞进 track，
 * 再量 `offsetLeft/offsetWidth` 定位。这套做法与 React 有两处硬冲突：
 *   ① SSR 首屏 HTML 里没有克隆节点，客户端 hydrate 后才凭空多出 16 个 —— hydration 不匹配，
 *      表现为首屏闪烁/错位；
 *   ② React 重渲染会把手工插入的 DOM 抹掉。
 * 故改为：**渲染期就在 JSX 里输出 3 份卡片**（[克隆][真实][克隆]，中间那份是"真"），
 * JS 只负责 transform 位移。单测 CASE-R2 断言 SSR 输出恰好 24 张卡（8×3）守住这一点。
 *
 * 【监听与定时器必须清理】原型注册了 mousemove / mouseup / resize / visibilitychange 四个监听
 * 和一个 setInterval 自动播放，全文 `removeEventListener` **0 处** —— 纯泄漏，单页应用里每次进出
 * 该页都会叠加一层。此处一律在 useEffect 的清理函数里解绑。
 *
 * 【SSR 安全】所有 window/document 访问都在 useEffect 内；SSR 阶段只输出静态结构，
 * 初始 index 指向中间那份的第 0 张，保证首屏与 hydrate 后一致。
 */
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { IconChevronLeft, IconChevronRight } from './Icons';

/** 自动播放间隔（与原型 setInterval 一致） */
const AUTOPLAY_MS = 3500;
/** 位移动画时长，需与 .cj-slider-track 的 transition 时长保持一致 */
const TRANSITION_MS = 500;

export default function EventsSlider({ data }) {
  const slides = (data && data.slides) || [];
  const total = slides.length;

  // [克隆][真实][克隆] 三份；真实份的下标区间是 [total, 2*total)
  const tripled = useMemo(
    () => (total ? slides.concat(slides).concat(slides) : []),
    [slides, total]
  );

  // 初始停在中间那份的第 0 张：SSR 与首帧一致，左右都还有一份可滚，不会一动就露边
  const [index, setIndex] = useState(total);
  const [animating, setAnimating] = useState(true);
  // 轨道位移（px）。null = 尚未测量（SSR 首屏与 hydrate 首帧），此时不写 transform。
  const [translate, setTranslate] = useState(null);

  const trackRef = useRef(null);
  const viewportRef = useRef(null);
  const timerRef = useRef(null);
  const jumpTimerRef = useRef(null);

  const go = useCallback((delta) => {
    if (!total) return;
    setAnimating(true);
    setIndex((i) => i + delta);
  }, [total]);

  // 越界后"无缝跳位"：动画结束的瞬间关掉过渡、把下标折回中间那份，视觉上看不出跳变
  useEffect(() => {
    if (!total) return undefined;
    if (index >= total * 2 || index < total) {
      jumpTimerRef.current = setTimeout(() => {
        setAnimating(false);
        setIndex((i) => ((i % total) + total));
      }, TRANSITION_MS);
    }
    return () => {
      if (jumpTimerRef.current) clearTimeout(jumpTimerRef.current);
    };
  }, [index, total]);

  // 关掉过渡后立刻在下一帧恢复，否则后续点击不再有动画
  useEffect(() => {
    if (animating) return undefined;
    const raf = requestAnimationFrame(() => setAnimating(true));
    return () => cancelAnimationFrame(raf);
  }, [animating]);

  /**
   * 把当前 active 卡**居中**到视口 —— 与原型 getTranslate() 同一套算法：
   *   translate = viewport.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2)
   *
   * 【为什么必须实测而不能用 CSS 变量算】卡片宽度在不同断点下由 CSS 决定（PC 210px、
   * ≤767 时是 68vw 且 max-width:270px），active 卡还有 scale 放大；步长不是常量。
   * 早先用 `translate3d(calc(-index * var(--cj-slide-step, 0px)))` 且从未定义该变量，
   * 回退值 0px 让轨道**根本不移动** —— 页面上永远停在最左边那几张克隆卡上，
   * active 卡在视口外，三态缩放也就看不见（260818 与原型比对时肉眼发现）。
   *
   * offsetLeft/offsetWidth 是布局值，不受 transform:scale 影响，故测量稳定、无需等动画结束。
   */
  const measure = useCallback(() => {
    const viewport = viewportRef.current;
    const track = trackRef.current;
    if (!viewport || !track) return;
    const card = track.children[index];
    if (!card) return;
    setTranslate(viewport.clientWidth / 2 - (card.offsetLeft + card.offsetWidth / 2));
  }, [index]);

  useLayoutEffect(() => { measure(); }, [measure]);

  // 视口尺寸变化、以及图片加载完成（图片撑开卡片会改变 offsetLeft）后都要重测
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && viewportRef.current) {
      ro = new ResizeObserver(onResize);
      ro.observe(viewportRef.current);
    }
    // 原型在 rAF 里只测一次，SSR + 懒加载下容易测早；这里补一次 window load 兜底
    window.addEventListener('load', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('load', onResize);
      if (ro) ro.disconnect();
    };
  }, [measure]);

  // 自动播放：hover 暂停、标签页隐藏时暂停（与原型行为一致），卸载时清理
  useEffect(() => {
    if (typeof window === 'undefined' || !total) return undefined;

    const viewport = viewportRef.current;
    let paused = false;

    const tick = () => { if (!paused) go(1); };
    timerRef.current = setInterval(tick, AUTOPLAY_MS);

    const onEnter = () => { paused = true; };
    const onLeave = () => { paused = false; };
    const onVisibility = () => { paused = document.hidden; };

    if (viewport) {
      viewport.addEventListener('mouseenter', onEnter);
      viewport.addEventListener('mouseleave', onLeave);
    }
    document.addEventListener('visibilitychange', onVisibility);

    // 原型这几个监听从不解绑（removeEventListener 0 处），此处必须清理
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (viewport) {
        viewport.removeEventListener('mouseenter', onEnter);
        viewport.removeEventListener('mouseleave', onLeave);
      }
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [go, total]);

  if (!data || !total) return null;

  // 真实份内的位置，用于三态类（active / near / 其余）
  const realIndex = ((index % total) + total) % total;

  // 未测量前（SSR 与 hydrate 首帧）不写 transform，避免服务端/客户端属性不一致
  const trackStyle = translate === null
    ? undefined
    : {
        transform: `translate3d(${translate}px, 0, 0)`,
        transition: animating ? `transform ${TRANSITION_MS}ms cubic-bezier(.22,.61,.36,1)` : 'none',
      };

  return (
    <section className="cj-suggestion-slider">
      <div className="cj-suggestion-inner">
        <div className="cj-suggestion-head">
          {/* 【相邻文本节点合成单个表达式】本仓 SSR 产物会被剥掉全部 HTML 注释，而 React 靠 `<!-- -->`
              分隔相邻文本节点；注释被剥后 hydrate 会报 `Text content did not match`。
              故这里把前后空格并入同一个字符串表达式，不写成 `{data.titlePre}{' '}` 那种两节点形式。 */}
          <h2 className="cj-suggestion-title">
            {data.titlePre + ' '}
            <span className="cj-suggestion-title-em">{data.titleEm1}</span>
            {' ' + data.titleMid + ' '}
            {/* 【D7⑤】原型此处用内联 style="color:#ff0051"，内联样式进不了 :global 体系，改用类 */}
            <span className="cj-suggestion-title-em cj-suggestion-title-em--pink">{data.titleEm2}</span>
            {' ' + data.titlePost}
          </h2>
        </div>

        <div className="cj-slider-area">
          <button
            type="button"
            className="cj-slider-btn cj-slider-prev"
            onClick={() => go(-1)}
            aria-label={data.prevAria}
          >
            <IconChevronLeft />
          </button>

          <div className="cj-slider-viewport" ref={viewportRef}>
            <div className="cj-slider-track" ref={trackRef} style={trackStyle}>
              {tripled.map((s, i) => {
                // 中间那份才是"真"卡，前后两份是克隆（与原型 cloneNode 出来的语义一致）
                const isClone = i < total || i >= total * 2;
                const pos = ((i % total) + total) % total;
                // 【按绝对下标判定三态，不能按 pos】pos 是"折回真实份"后的位置，同一个 pos 在三份里各有
                // 一张卡，用 `pos === realIndex` 会同时点亮 3 张（260818 与原型比对实测：原型
                // activeCount=1、我这边=3，卡片被放大了 3 处）。改为与当前绝对下标 index 比较，
                // 全轨道有且仅有一张 active、其左右各一张 near —— 与原型一致。
                const cls = [
                  'cj-slide-card',
                  isClone ? 'cj-clone' : '',
                  i === index ? 'active' : '',
                  Math.abs(i - index) === 1 ? 'near' : '',
                ].filter(Boolean).join(' ');
                return (
                  <div
                    className={cls}
                    key={i}
                    data-real-index={pos}
                    aria-hidden={isClone ? 'true' : undefined}
                  >
                    <span className="cj-slide-number">{s.num}</span>
                    {/* 【不能加 loading="lazy"】原型这 8 张轮播图就是 eager 的（其余静态图才 lazy）。
                        横向轮播里绝大多数卡片始终在 viewport 之外，lazy 会让它们永远不加载——
                        260818 实测：加 lazy 时 24 张卡中有 18 张 naturalWidth=0，点下一张就是空白卡。 */}
                    <img
                      src={s.imageUrl}
                      alt={[data.slideAlt, s.num].filter(Boolean).join(' ')}
                      draggable="false"
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <button
            type="button"
            className="cj-slider-btn cj-slider-next"
            onClick={() => go(1)}
            aria-label={data.nextAria}
          >
            <IconChevronRight />
          </button>
        </div>

        {/* 【定宽分段 + 平移，不是增长型进度条】与原型 updateProgress() 一致：
            width 恒为 100/total（8 张即 12.5%），靠 translateX(realIndex * 100%) 滑到对应档位。
            我最初写成 width=(realIndex+1)/total 的"进度增长"效果，与原型的"切换指示"语义不同。 */}
        <div className="cj-slider-progress">
          <div
            className="cj-slider-progress-bar"
            style={{
              width: `${100 / total}%`,
              transform: `translateX(${realIndex * 100}%)`,
            }}
          />
        </div>
      </div>
    </section>
  );
}
